import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import apiAxios from 'helpers/apiAxios';
import { getPaymentRequest } from 'redux/erp/paymentRequestSlice';
import { Button } from 'components/common';

export default function FinanceCompletionCard({ paymentRequest: pr, embedded = false }) {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [followUp, setFollowUp] = useState(pr.finance_follow_up || {});
  useEffect(() => setFollowUp(pr.finance_follow_up || {}), [pr.finance_follow_up]);
  const action = async (name, data = {}) => {
    setBusy(true);
    try {
      await apiAxios.post(`/erp/payment_requests/${pr.id}/${name}`, data);
      await dispatch(getPaymentRequest(pr.id)).unwrap();
      toast.success(name === 'hard_copy_receipt' ? 'Hard copy receipt updated.' : 'Certificate signing status updated.');
    } catch (error) { toast.error(error?.response?.data?.error || error?.message || 'Action failed.'); }
    finally { setBusy(false); }
  };
  const bundle = async () => {
    setBusy(true);
    try {
      const res = await apiAxios.get(`/erp/payment_requests/${pr.id}/document_bundle`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${pr.code}-documents.zip`;
      anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Cannot download all documents. Check Doc Search / storage and retry.'); }
    finally { setBusy(false); }
  };
  return <div className={embedded ? "tw-space-y-3" : "tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-bg-white tw-p-5 tw-space-y-3"}>
    {!embedded && <div className="tw-font-semibold tw-text-sm">Finance workflow & documents</div>}
    <p className="tw-text-sm tw-m-0">{pr.payment_summary || pr.note || '—'}</p>
    {pr.accountant_review?.name && <p className="tw-text-xs tw-m-0">Accountant review: {pr.accountant_review.name}</p>}
    {pr.payment_counterparty === 'employee' && <p className="tw-text-xs tw-text-geist-warning tw-m-0">Employee reimbursement: prepare Journal and use Non-HSBC. The current HSBC API scope excludes employee refunds.</p>}
    {pr.permissions?.allow_finance_insight && pr.certificate_approval?.bank_mode === 'pre_authorised' && <p className="tw-text-xs tw-text-geist-fg-3 tw-m-0">HSBC Pre-authorised: Finance and BOD authorise in Portal. After the final BOD signature, the system automatically sends XML v3 signed/encrypted with PGP.</p>}
    <Button size="sm" variant="secondary" disabled={busy} onClick={bundle}>Download all documents · ZIP</Button>
    {pr.permissions?.allow_hard_copy_receipt && <label className="tw-block tw-text-xs"><input aria-label="Hard copy received" type="checkbox" disabled={busy} checked={!!followUp.hard_copy_received}
      onChange={(e) => action('hard_copy_receipt', { received: e.target.checked })} /> Hard copy received by Accounting</label>}
    {/* SmartCA signing moved to SmartCaSignCard on the request page (send again on expiry). */}
  </div>;
}

FinanceCompletionCard.propTypes = {
  paymentRequest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    code: PropTypes.string,
    note: PropTypes.string,
    payment_summary: PropTypes.string,
    payment_counterparty: PropTypes.string,
    finance_follow_up: PropTypes.shape({
      hard_copy_received: PropTypes.bool,
    }),
    accountant_review: PropTypes.shape({
      name: PropTypes.string,
    }),
    certificate_approval: PropTypes.shape({
      bank_mode: PropTypes.string,
    }),
    permissions: PropTypes.shape({
      allow_finance_insight: PropTypes.bool,
      allow_hard_copy_receipt: PropTypes.bool,
    }),
  }).isRequired,
  embedded: PropTypes.bool,
};
